import { Box, IconButton, Stack } from '@mui/material'
import { observer } from 'mobx-react'
import React from 'react'
import { Path, useFormContext } from 'react-hook-form'
import FormField from '../../../../../components/Form/FormField'
import { getImageSrc } from '../../../../../helpers/getImageSrc'
import { placeNames } from '../../../../../helpers/globalVariables'
import { useTerritoryFields } from '../../../../../hooks/useTerritoryFields'
import { getComp } from '../../../../../utils/utils/DI'
import FormAutocomplete from '../../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormAutocomplete'
import FormSelect from '../../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormSelect'
import TypedAccordion from '../../../../SingleRequestPage/components/SingleRequestDrawer/elements/TypedAccordion'
import { checkDefaultValues } from '../consts/checkDrawer.consts'

interface EquipmentFormValues {
  phoneNumber: string
  address: string
  equipmentId: string
  preferentialCategories: boolean[]
  surname: string
  name: string
  patronymic: string
  territory: string
  district: string
  locality: string
  addressType: string
  house: string
  building: string
  apartment: string
  created: string
  additionalInfo: string
  senderId: string
  isUrgent: boolean
  orderId: number | null
  [key: string]: any // Для других возможных полей формы
}

const RequestDataAccordion = <T extends EquipmentFormValues>() => {
  const { control, watch, setValue } = useFormContext<T>()

  const defaultValues = checkDefaultValues

  const territoryStore = getComp('TerritoryStore')
  const { shownSenders } = getComp('SendersStore')

  const {
    categoriesItems,
    onCategoriesChange,
    districtOptions,
    localitiesOptions,
    locality,
    district,
  } = useTerritoryFields(watch, setValue, -1, true)

  const isCategoriesAdded = !watch('preferentialCategories' as Path<T>)[
    watch('preferentialCategories' as Path<T>)?.length - 1
  ]

  return (
    <TypedAccordion summary={'Данные абонента'} customOpen>
      <Stack paddingTop={'8px'} gap={'14px'}>
        <FormField
          control={control}
          name={'surname' as Path<T>}
          fullWidth
          label='Фамилия'
          error={!watch('surname' as Path<T>).trim()}
          capitalizeFirstLetter
        />
        <FormField
          control={control}
          name={'name' as Path<T>}
          fullWidth
          label='Имя'
          error={!watch('name' as Path<T>).trim()}
          capitalizeFirstLetter
        />
        <FormField
          control={control}
          name={'patronymic' as Path<T>}
          fullWidth
          label='Отчество'
          error={!watch('patronymic' as Path<T>).trim()}
          capitalizeFirstLetter
        />
        <FormField
          control={control}
          name={'phoneNumber' as Path<T>}
          fullWidth
          label='Телефон'
          mask={'+7 (999)-999-99-99'}
          error={!watch('phoneNumber' as Path<T>)}
        />
        <FormAutocomplete
          control={control}
          fullWidth
          name={'territory' as Path<T>}
          options={territoryStore.collection as string[]}
          withoutTyping
          label='Регион'
          error={!watch('territory' as Path<T>)}
        />
        <FormAutocomplete
          control={control}
          fullWidth
          name={'district' as Path<T>}
          options={districtOptions}
          label='Округ'
          freeSolo
          disabled={!watch('territory' as Path<T>)}
          error={
            !!watch('territory' as Path<T>) &&
            (!watch('district' as Path<T>) ||
              !districtOptions?.includes(district))
          }
        />
        <FormAutocomplete
          control={control}
          fullWidth
          name={'locality' as Path<T>}
          options={localitiesOptions}
          label='Населённый пункт'
          fullDotationRegions={[]}
          freeSolo
          disabled={!watch('district' as Path<T>)}
          error={
            !!watch('district' as Path<T>) &&
            (!watch('locality' as Path<T>) ||
              !localitiesOptions?.includes(locality))
          }
        />
        <FormAutocomplete
          control={control}
          fullWidth
          name={'addressType' as Path<T>}
          options={placeNames}
          freeSolo
          label='Топоним'
        />

        <FormField
          control={control}
          name={'address' as Path<T>}
          fullWidth
          label='Наименование топонима (улицы / переулка и т.д.) '
          error={!watch('address' as Path<T>).trim()}
        />
        <Stack direction={'row'} spacing={'10px'} maxWidth={'100%'}>
          <FormField
            control={control}
            fullWidth
            name={'house' as Path<T>}
            label='Дом'
            onlyNumbers
          />
          <FormField
            control={control}
            fullWidth
            name={'building' as Path<T>}
            label='Корпус'
            onlyNumbersAndLetters
          />

          <FormField
            control={control}
            fullWidth
            name={'apartment' as Path<T>}
            label='Квартира'
            onlyNumbers
          />
        </Stack>
        <Box position={'relative'}>
          <FormSelect
            control={control}
            fullWidth
            items={categoriesItems}
            name={'preferentialCategories' as Path<T>}
            label='Льготная категория'
            multiple
            onChange={onCategoriesChange}
            disableDefaultOnChange
            withLabel
            width={'340px'}
          />
          {isCategoriesAdded && (
            <IconButton
              sx={{
                position: 'absolute',
                top: '15%',
                right: '35px',
                padding: '4px',
              }}
              onClick={() =>
                setValue(
                  'preferentialCategories' as Path<T>,
                  (defaultValues?.preferentialCategories ?? []) as T[Path<T>],
                )
              }
            >
              <img src={getImageSrc('close')} style={{ width: '18px' }} />
            </IconButton>
          )}
        </Box>
        <FormAutocomplete
          control={control}
          fullWidth
          name={'senderId' as Path<T>}
          withoutTyping
          options={shownSenders.map((s: any) => s.name)}
          label='Источник данных о заявке'
        />
        <FormField
          control={control}
          name={'additionalInfo' as Path<T>}
          fullWidth
          placeholder='Дополнительная информация / краткое описание причины обращения'
          label='Дополнительная информация'
          multiline
          rows={3}
        />
      </Stack>
    </TypedAccordion>
  )
}

export default observer(RequestDataAccordion)
